import React, { useState, useEffect, useCallback } from 'react';
import Icon from 'components/AppIcon';
import { useChat } from '../../../hooks/useChat';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

const FALLBACK_OPPORTUNITIES = [
  { rank: 1, category: 'Dining', monthlySaving: 80, steps: ['Cook at home 3 more nights per week', 'Use meal prep on Sundays to reduce impulse orders', 'Set a $150/month dining budget cap'], goalImpact: 'Accelerates your top goal by ~2 months', color: '#F59E0B', icon: 'Utensils' },
  { rank: 2, category: 'Entertainment', monthlySaving: 45, steps: ['Audit and cancel unused streaming subscriptions', 'Use free library resources for books and movies', 'Set a monthly entertainment budget alert'], goalImpact: 'Adds $540/year toward your savings goals', color: '#7B2FBE', icon: 'Tv' },
  { rank: 3, category: 'Shopping', monthlySaving: 60, steps: ['Implement a 48-hour rule before non-essential purchases', 'Use cashback apps for regular purchases', 'Unsubscribe from promotional emails'], goalImpact: 'Reduces impulse spending by an estimated 30%', color: '#00E5FF', icon: 'ShoppingBag' },
];

export default function SavingsOpportunities({ opportunities, spendingData }) {
  const { user } = useAuth();
  const [aiOpportunities, setAiOpportunities] = useState([]);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [goals, setGoals] = useState([]);
  const { response, isLoading, error, sendMessage } = useChat('OPEN_AI', 'gpt-5', false);

  useEffect(() => {
    if (!user?.id) return;
    supabase?.from('financial_goals')?.select('title, target_amount, current_amount, monthly_contribution, deadline, goal_status')?.eq('user_id', user?.id)?.eq('goal_status', 'active')?.then(({ data }) => { if (data) setGoals(data); });
  }, [user?.id]);

  const generateOpportunities = useCallback(() => {
    if (isLoading) return;
    setHasGenerated(true);

    const categoryData = (spendingData || opportunities || [])?.map(d => {
      const change = d?.lastMonth > 0 ? (((d?.thisMonth - d?.lastMonth) / d?.lastMonth) * 100)?.toFixed(1) : '0';
      return `${d?.category || d?.name}: $${d?.thisMonth || d?.yourSpend} (${change > 0 ? '+' : ''}${change}% vs last month)`;
    })?.join('\n');

    const goalsData = goals?.map(g => {
      const remaining = parseFloat(g?.target_amount) - parseFloat(g?.current_amount || 0);
      const monthly = parseFloat(g?.monthly_contribution || 0);
      const months = monthly > 0 ? Math.ceil(remaining / monthly) : null;
      return `${g?.title}: $${parseFloat(g?.current_amount || 0)?.toFixed(0)} saved of $${parseFloat(g?.target_amount)?.toFixed(0)} target${months ? `, ~${months} months to complete` : ''}`;
    })?.join('\n');

    sendMessage([
      {
        role: 'system',
        content: `You are FinVisAR, a personal finance AI. Analyze spending data and return ONLY a valid JSON array of exactly 3 savings opportunity objects. Each object must have: "rank" (1, 2, or 3), "category" (spending category name), "monthlySaving" (estimated monthly savings as a number), "steps" (array of exactly 2-3 specific actionable strings), "goalImpact" (1 sentence describing impact on user's financial goals with specific timeframe if possible), "color" (one of: "#F59E0B", "#7B2FBE", "#00E5FF", "#10B981", "#EF4444"), "icon" (one of: "Utensils", "ShoppingBag", "Tv", "Car", "Coffee", "Smartphone", "Home", "DollarSign"). Return ONLY the JSON array.`,
      },
      {
        role: 'user',
        content: `Identify the top 3 savings opportunities from my spending data:\n\nSpending:\n${categoryData}\n\nActive Goals:\n${goalsData || 'No active goals set'}\n\nRank by highest potential monthly savings. Be specific with dollar amounts and goal impact timelines.`,
      },
    ], { max_completion_tokens: 1000 });
  }, [isLoading, spendingData, opportunities, goals, sendMessage]);

  useEffect(() => {
    if (response && hasGenerated && !isLoading) {
      try {
        const match = response?.match(/\[.*\]/s);
        if (match) {
          const parsed = JSON.parse(match?.[0]);
          if (Array.isArray(parsed) && parsed?.length > 0) {
            setAiOpportunities(parsed);
            return;
          }
        }
        setAiOpportunities(FALLBACK_OPPORTUNITIES);
      } catch {
        setAiOpportunities(FALLBACK_OPPORTUNITIES);
      }
    }
  }, [response, hasGenerated, isLoading]);

  useEffect(() => {
    if (error && hasGenerated) setAiOpportunities(FALLBACK_OPPORTUNITIES);
  }, [error, hasGenerated]);

  const displayOpps = hasGenerated ? aiOpportunities : (opportunities?.slice(0, 3) || []);

  return (
    <div className="bg-[rgba(255,255,255,0.03)] border border-[rgba(0,229,255,0.1)] rounded-xl overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(0,229,255,0.1)' }}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-[rgba(245,158,11,0.15)]">
            <Icon name="Lightbulb" size={15} color="#F59E0B" />
          </div>
          <div>
            <h3 className="text-[#E2E8F0] font-semibold text-sm">Savings Opportunities</h3>
            <p className="text-[#64748B] text-xs">AI-powered suggestions</p>
          </div>
        </div>
        <button
          onClick={generateOpportunities}
          disabled={isLoading}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[rgba(245,158,11,0.1)] border border-[rgba(245,158,11,0.2)] text-[#F59E0B] text-xs font-medium hover:bg-[rgba(245,158,11,0.18)] transition-colors disabled:opacity-50"
        >
          {isLoading ? (
            <><div className="w-3 h-3 border border-[#F59E0B] border-t-transparent rounded-full animate-spin" />Analyzing...</>
          ) : (
            <><Icon name="Sparkles" size={12} />{hasGenerated ? 'Refresh' : 'AI Analyze'}</>
          )}
        </button>
      </div>
      <div className="p-5">
        {/* Loading skeleton */}
        {isLoading && (
          <div className="space-y-3">
            {[1, 2, 3]?.map(i => (
              <div key={i} className="p-4 rounded-xl bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.06)] animate-pulse">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[rgba(255,255,255,0.06)] flex-shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3 bg-[rgba(255,255,255,0.06)] rounded w-1/2" />
                    <div className="h-2.5 bg-[rgba(255,255,255,0.04)] rounded w-full" />
                    <div className="h-2.5 bg-[rgba(255,255,255,0.04)] rounded w-4/5" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Static fallback (before AI) */}
        {!isLoading && !hasGenerated && opportunities?.length === 0 && (
          <div className="text-center py-8">
            <Icon name="Lightbulb" size={28} color="#475569" />
            <p className="text-[#64748B] text-sm mt-2 mb-3">No spending data yet. Add transactions to see savings opportunities.</p>
          </div>
        )}

        {/* Static opportunities (before AI) */}
        {!isLoading && !hasGenerated && opportunities?.length > 0 && (
          <div className="space-y-3">
            {opportunities?.slice(0, 3)?.map((opp, idx) => (
              <div key={opp?.category} className="flex items-start gap-3 p-3 rounded-xl bg-[rgba(245,158,11,0.05)] border border-[rgba(245,158,11,0.15)]">
                <div className="w-7 h-7 rounded-lg bg-[rgba(245,158,11,0.15)] flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs font-bold text-[#F59E0B]">#{idx + 1}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[#E2E8F0] text-sm font-medium">{opp?.category}</span>
                    <span className="text-[#EF4444] text-xs font-mono">+{opp?.overPct?.toFixed(0)}% vs avg</span>
                  </div>
                  <p className="text-[#64748B] text-xs">{opp?.suggestion}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* AI-generated opportunities */}
        {!isLoading && hasGenerated && displayOpps?.length > 0 && (
          <div className="space-y-3">
            {displayOpps?.map((opp, idx) => (
              <div
                key={idx}
                className="p-4 rounded-xl border transition-all duration-200"
                style={{ backgroundColor: `${opp?.color || '#F59E0B'}08`, borderColor: `${opp?.color || '#F59E0B'}25` }}
              >
                <div className="flex items-start gap-3">
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${opp?.color || '#F59E0B'}15` }}
                  >
                    <span className="text-xs font-bold" style={{ color: opp?.color || '#F59E0B' }}>#{opp?.rank || idx + 1}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <Icon name={opp?.icon || 'DollarSign'} size={13} color={opp?.color || '#F59E0B'} />
                        <span className="text-[#E2E8F0] text-sm font-semibold">{opp?.category}</span>
                      </div>
                      <span
                        className="text-xs font-bold font-mono px-2 py-0.5 rounded-full"
                        style={{ color: '#10B981', backgroundColor: 'rgba(16,185,129,0.12)' }}
                      >
                        Save ${opp?.monthlySaving}/mo
                      </span>
                    </div>
                    {/* Actionable steps */}
                    <div className="space-y-1 mb-2">
                      {opp?.steps?.map((step, si) => (
                        <div key={si} className="flex items-start gap-1.5">
                          <div className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0" style={{ backgroundColor: opp?.color || '#F59E0B' }} />
                          <p className="text-[#94A3B8] text-xs leading-relaxed">{step}</p>
                        </div>
                      ))}
                    </div>
                    {/* Goal impact */}
                    <div className="flex items-center gap-1.5 p-2 rounded-lg" style={{ backgroundColor: 'rgba(0,229,255,0.06)', border: '1px solid rgba(0,229,255,0.12)' }}>
                      <Icon name="Target" size={11} color="#00E5FF" />
                      <p className="text-[#00E5FF] text-[11px] font-medium">{opp?.goalImpact}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
